import React from 'react';
import PrimePickBadge from '../../ui/PrimePickBadge';

/** One race from `topPickWinnersDetails` (position-1 top picks only). */
export interface WinnerRace {
  trackName?: string | null;
  raceNumber?: number | null;
  /** Every analysis agreed on this pick: a Prime Pick. */
  isConsensus?: boolean | null;
  topPickWinner?: {
    horseName?: string | null;
    horseNumber?: number | null;
    winningPrice?: number | null;
    assessedOdds?: number | null;
    marketOdds?: number | null;
  } | null;
}

export const formatPrice = (price: number | null | undefined): string =>
  price == null || isNaN(price) ? '—' : `$${price.toFixed(2)}`;

/** "4. Horse Name", or just the name when the saddlecloth is missing. */
export const horseLabel = (w: WinnerRace['topPickWinner']): string =>
  !w?.horseName ? '—' : w.horseNumber ? `${w.horseNumber}. ${w.horseName}` : w.horseName;

const COLS = 'grid grid-cols-[1fr_0.5fr_1.5fr_1.1fr_0.6fr_0.6fr] gap-2.5 px-5 py-4';

/**
 * Recent winners and tracked results: real top-pick winners, never made-up
 * rows. Six columns, so it scrolls sideways on a phone rather than squashing.
 */
const RecentWinners: React.FC<{ races?: WinnerRace[]; loading?: boolean }> = ({ races = [], loading = false }) => {
  const rows = races.filter((r) => r.topPickWinner);

  return (
    <div id="results" className="px-4 py-10 bg-racing-gray border-t-4 border-racing-green">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl font-heading font-bold text-deep-navy mb-1 text-center">Recent winners and tracked results</h2>
        <p className="text-center text-gray-500 text-sm mb-6">Real results from our top picks, not promises.</p>

        <div className="rounded-xl overflow-x-auto bg-white border border-racing-gray-darker shadow-card">
          <div className="min-w-[720px]">
            <div className={`${COLS} bg-deep-navy font-bold text-white text-sm`}>
              <span>Venue</span>
              <span>Race</span>
              <span>Horse</span>
              <span>Selection</span>
              <span>Result</span>
              <span>Price</span>
            </div>

            {loading ? (
              <div className="px-5 py-8 text-center text-gray-500">Loading recent winners…</div>
            ) : rows.length === 0 ? (
              <div className="px-5 py-8 text-center text-gray-500">Recent winners will appear here as results are confirmed.</div>
            ) : (
              rows.map((race, i) => (
                <div key={`${race.trackName ?? 'race'}-${race.raceNumber ?? i}-${i}`} className={`${COLS} border-t border-racing-gray-darker text-gray-700 items-center`}>
                  <span>{race.trackName ?? '—'}</span>
                  <span>{race.raceNumber ? `R${race.raceNumber}` : '—'}</span>
                  <span className="font-bold text-deep-navy">{horseLabel(race.topPickWinner)}</span>
                  <span className="flex items-center gap-2 flex-wrap">
                    Top Pick
                    {race.isConsensus && <PrimePickBadge />}
                  </span>
                  <span className="text-racing-green-dark font-bold">Won</span>
                  <span>{formatPrice(race.topPickWinner?.winningPrice)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentWinners;
